import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, MapPin, Truck, Package, Phone, ChevronRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import InteractiveMap from '@/components/InteractiveMap';
import AIReadabilityBlock from '@/components/AIReadabilityBlock';

const ServicesPage = () => {
  const { language, t } = useLanguage();

  const services = [
    {
      key: 'sod-delivery',
      title: language === 'en' ? 'Sod Delivery Service' : 'Servicio de Entrega de Césped',
      description: language === 'en' 
        ? 'Fresh cut grass delivery and professional installation throughout Montgomery County'
        : 'Entrega de césped recién cortado e instalación profesional en el Condado de Montgomery',
      icon: Truck,
      href: `/${language}/services/sod-delivery`,
      features: language === 'en' 
        ? ['Fresh cut grass delivery', 'Professional installation', 'Proper ground preparation', 'Expert guidance on care']
        : ['Entrega de césped recién cortado', 'Instalación profesional', 'Preparación adecuada del terreno', 'Asesoramiento experto sobre cuidado'],
      price: language === 'en' ? 'Quote on request' : 'Cotización bajo solicitud',
      timing: language === 'en' ? 'Fast delivery available' : 'Entrega rápida disponible'
    },
    {
      key: 'bulk-delivery',
      title: language === 'en' ? 'Bulk Materials Delivery' : 'Entrega de Materiales a Granel',
      description: language === 'en'
        ? 'Large quantity delivery of soil, rock, mulch, and sand for commercial and residential projects'
        : 'Entrega de grandes cantidades de tierra, roca, mantillo y arena para proyectos comerciales y residenciales',
      icon: Package,
      href: `/${language}/services/bulk-delivery`,
      features: language === 'en'
        ? ['Bulk quantities', 'Commercial rates', 'Flexible scheduling', 'Multiple dump locations']
        : ['Cantidades a granel', 'Tarifas comerciales', 'Horarios flexibles', 'Múltiples ubicaciones de descarga'],
      price: language === 'en' ? 'Volume pricing available' : 'Precios por volumen disponibles',
      timing: language === 'en' ? '24-48 hour delivery' : 'Entrega en 24-48 horas'
    },
  ];

  const serviceAreas = [
    'The Woodlands TX', 'Magnolia TX', 'Conroe TX', 'Cypress TX',
    'Tomball TX', 'Spring TX', 'Willis TX', 'Montgomery County TX'
  ];

  const seoTitle = language === 'en' 
    ? 'Professional Landscaping Services Texas | Sod & Material Delivery'
    : 'Servicios Profesionales de Paisajismo Texas | Entrega de Césped y Materiales';
  
  const seoDescription = language === 'en'
    ? 'Professional landscaping services in Montgomery County TX. Sod installation, bulk material delivery, landscape consultation. Licensed & insured.'
    : 'Servicios profesionales de paisajismo en el Condado Montgomery TX. Instalación de césped, entrega de materiales a granel, consultoría de paisaje. Licenciado y asegurado.';
  
  const seoKeywords = language === 'en'
    ? 'landscaping services texas, sod installation montgomery county, bulk delivery conroe, landscape consultation woodlands, material delivery texas'
    : 'servicios paisajismo texas, instalación césped montgomery county, entrega granel conroe, consultoría paisaje woodlands, entrega materiales texas';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": seoTitle,
    "description": seoDescription,
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "TX",
        "addressLocality": "Montgomery County"
      },
      "areaServed": [
        "Montgomery County TX", "The Woodlands TX", "Conroe TX", "Cypress TX"
      ]
    },
    "serviceType": [
      "Sod Installation", "Material Delivery", "Landscape Consultation"
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/services' : '/es/servicios'}
        structuredData={structuredData}
        language={language}
      />
      <Header />
      
      <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 animate-pulse"></div>
        <div className="container mx-auto px-6 text-center relative">
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {language === 'en' ? 'Our Services' : 'Nuestros Servicios'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 animate-fade-in delay-200">
              {language === 'en' 
                ? 'Professional landscaping services and material delivery throughout Montgomery County'
                : 'Servicios profesionales de paisajismo y entrega de materiales en todo el Condado de Montgomery'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in delay-400">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base hover:bg-white/30 transition-all hover-scale">
                <CheckCircle className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Fresh Cut from Farm' : 'Recién Cortado del Campo'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base hover:bg-white/30 transition-all hover-scale">
                <Clock className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Quality & Service' : 'Calidad y Servicio'}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* AI Readability Block - Optimized for LLM extraction */}
      <AIReadabilityBlock variant="services" />

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Professional Services' : 'Servicios Profesionales'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'From delivery to installation, we provide comprehensive landscaping solutions'
                : 'Desde entrega hasta instalación, proporcionamos soluciones integrales de paisajismo'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card key={service.key} className="group hover:shadow-glow transition-all duration-300 border-2 hover:border-primary">
                  <CardHeader className="text-center pb-4">
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-muted-foreground">
                          {language === 'en' ? 'Starting from:' : 'Desde:'}
                        </span>
                        <span className="text-lg font-bold text-primary">{service.price}</span>
                      </div>
                      
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 mr-2 text-primary" />
                        {service.timing}
                      </div>

                      <ul className="space-y-2">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <Link to={service.href}>
                        <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          {language === 'en' ? 'Learn More' : 'Saber Más'}
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interactive Service Areas Map */}
      <InteractiveMap />

      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' 
              ? 'Ready to Start Your Project?' 
              : '¿Listo para Comenzar tu Proyecto?'
            }
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Get a free quote today and let our experts help you choose the right materials and services.'
              : 'Obtén una cotización gratuita hoy y deja que nuestros expertos te ayuden a elegir los materiales y servicios correctos.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/quote`}>
              <Button variant="hero" size="lg">
                {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-secondary-foreground/20 text-green-700 hover:bg-secondary-foreground/10">
                <Phone className="mr-2 h-5 w-5" />
                {language === 'en' ? 'Call (936) 320-0763' : 'Llamar (936) 320-0763'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;